/* eslint-disable padding-line-between-statements */
import { ModalBody, ModalFooter, ModalHeader, ModalNext, Icon } from '@jutro/components';
import {
    validationDefaultProps,
    validationPropTypes,
    withValidation,
} from '@xengage/gw-portals-validation-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { OrganizationService } from 'gw-capability-gateway';
import { useTranslator } from '@jutro/locale';
import metadata from './EditAccountDetailsPopUp.metadata.json5';
import styles from './EditAccountDetailsPopUp.module.scss';
import messages from '../Accounts.messages';

import { Button } from '@jutro/legacy/components';

const EditAccountDetailsPopUp = props => {
    const {
        accountData,
        title,
        actionBtnLabel,
        cancelBtnLabel,
        isOpen,
        onResolve,
        onReject,
        isComponentValid,
        setComponentValidation,
        viewModelService,
        authHeader,
        authUserData
    } = props;
    const [accountDetailsVM, setAccountDetailsVM] = useState(null);
    const [primaryPhone, setPrimaryPhone] = useState('');
    const translator = useTranslator();
    const [emailValidationMessages, setEmailValidationMessage] = useState([]);
    const selectedUWCompany = _.get(authUserData, 'associationName_TWIA');
    const contactType = _.get(accountDetailsVM, 'contactType_TWIA.value.code');
    const [showErrors, setShowErrors] = useState(false);
    const ceUserName = _.get(accountDetailsVM, 'registeredCEUsername_TWIA.value');
    const emailAddress = _.get(accountDetailsVM, 'emailAddress1');
    const isEmailRequired = emailAddress && emailAddress?.aspects?.required
    let phoneTypeChanged = false

    const accountHolderVM = viewModelService.create(
        accountData.accountHolder,
        'pc',
        'edge.capabilities.policycommon.accountcontact.dto.AccountContactDTO'
    );

    function editNoteMessage() {
        return (
            <div>
                <span style={{ display: 'flex' }}>
                    <Icon id="icon" icon="gw-info"  color="#4074ce" />
                    <text style={{ paddingLeft: '6px'}}>{translator(messages.editNote)}</text>
                </span>
                
            </div>
        );
    };

    const writeValue = useCallback(
        (value, path) => {
            if (accountDetailsVM) {
                const model = viewModelService.clone(accountDetailsVM);

                _.set(model, path, value);
                setAccountDetailsVM(model);
            }
        },
        [accountDetailsVM, viewModelService]
    );

    const handleSave = useCallback(() => {
        const requestObject = {
            accountHolder: accountDetailsVM.value,
            accountNumber: accountData.accountNumber,
        };

        onResolve(requestObject);
    }, [onResolve, accountData, accountDetailsVM]);

    useEffect(() => {
        updatePrimaryPhoneFields();
    }, []);

    const updatePrimaryPhoneFields = (phoneTypeChanged) => {
        let primaryPhoneType = '';
        const primaryPhoneTypeCode = phoneTypeChanged ? _.get(accountDetailsVM, 'primaryPhoneType.value.code') :
            _.get(accountHolderVM, 'primaryPhoneType.value.code')

        switch (primaryPhoneTypeCode) {
            case 'work':
                primaryPhoneType = 'workNumber';
                break;
            case 'home':
                primaryPhoneType = 'homeNumber';
                break;
            case 'mobile':
                primaryPhoneType = 'cellNumber';
                break;
            default:
                primaryPhoneType = primaryPhone;
        }

        if (
            accountHolderVM.subtype.value === 'Company' &&
            primaryPhoneType === 'cellNumber'
        ) {
            primaryPhoneType = 'homeNumber';
        }
        if (!phoneTypeChanged) {
            setAccountDetailsVM(accountHolderVM);
            setPrimaryPhone(primaryPhoneType);
        } else {
            setAccountDetailsVM(accountDetailsVM);
            setPrimaryPhone(primaryPhoneType);
        }
    }

    const changeRequiredNumber = useCallback(
        (value, path) => {
            phoneTypeChanged = true
            _.set(accountDetailsVM, path, value);
            updatePrimaryPhoneFields(phoneTypeChanged);
            setAccountDetailsVM(accountDetailsVM);
        },
        [accountDetailsVM, phoneTypeChanged]
    );

    // Email validation to Display a warning message if Account Holder email is the same as any producer in the organization
    const validateEmail = useCallback((event ,fullValue) => {
        const { model, value } = fullValue;
        setEmailValidationMessage([]);
        _.set(accountDetailsVM, model, value);
        if (value) {
            try{
                OrganizationService.isProducerEmail_TWIA(value, authHeader).then(
                    (response) => {
                        if (response) {
                            setEmailValidationMessage([translator(messages.producerEmailError)]);
                        }
                    }
                ) 
            }catch(err){
                console.error(err);
            }
        }

        if (!value && (!_.isNil(ceUserName) || isEmailRequired)) {

            setEmailValidationMessage([translator(messages.Required)])
        } else if (emailAddress && !emailAddress.aspects.valid) {
            setEmailValidationMessage([translator(messages.emailValidationMessage)])
        }

        setAccountDetailsVM(accountDetailsVM);
    },[accountHolderVM, authHeader, translator, ceUserName, emailAddress, isEmailRequired]);


    const overrideProps = {
        '@field': {
            // showOptional: true,
            showErrors
        },
        primaryNumber: {
            onValueChange: changeRequiredNumber
        },
        workPhoneNumber: {
            required: primaryPhone === 'workNumber',
            showRequired: primaryPhone === 'workNumber',
        },
        homePhoneNumber: {
            required: primaryPhone === 'homeNumber',
            showRequired: primaryPhone === 'homeNumber',
        },
        cellPhoneNumber: {
            required: primaryPhone === 'cellNumber',
            showRequired: primaryPhone === 'cellNumber',
        },
        faxNumber: {
            visible: selectedUWCompany === 'twia' && contactType === 'company' // DE16485
        },
        email: {
            onBlur: validateEmail,
            validationMessages: emailValidationMessages,
            showRequired: !_.isNil(ceUserName) || isEmailRequired,
            required: !_.isNil(ceUserName) || isEmailRequired,
        },
        editNote:{
            content: editNoteMessage(),
            visible: selectedUWCompany === 'twia' // DE16473
        },
        mailingNote: {
            visible: selectedUWCompany === 'twia' // DE16519
        }
    };

    return (
        <ModalNext isOpen={isOpen} className={styles.editContactDetailsModal}>
            <ModalHeader title={title} onClose={onReject} />
            <ModalBody>
                <ViewModelForm
                    uiProps={metadata.pageContent}
                    model={accountDetailsVM}
                    overrideProps={overrideProps}
                    classNameMap={styles}
                    onValueChange={writeValue}
                    onValidationChange={setComponentValidation}
                />
            </ModalBody>
            <ModalFooter>
                <Button onClick={onReject} type="outlined">
                    {cancelBtnLabel}
                </Button>
                <Button onClick={handleSave} disabled={!isComponentValid}>
                    {actionBtnLabel}
                </Button>
            </ModalFooter>
        </ModalNext>
    );
};

EditAccountDetailsPopUp.propTypes = {
    viewModelService: PropTypes.shape({
        create: PropTypes.func.isRequired,
        clone: PropTypes.func.isRequired,
    }).isRequired,
    accountData: PropTypes.shape({
        accountNumber: PropTypes.string.isRequired,
        accountHolder: PropTypes.shape({
            primaryAddress: PropTypes.shape({}).isRequired,
            emailAddress1: PropTypes.shape({}).isRequired,
            subtype: PropTypes.shape({}).isRequired,
            cellNumber: PropTypes.string.isRequired,
            homeNumber: PropTypes.string.isRequired,
            workNumber: PropTypes.string.isRequired,
            primaryPhoneType: PropTypes.string,
        }).isRequired,
    }).isRequired,
    title: PropTypes.string.isRequired,
    actionBtnLabel: PropTypes.shape({}).isRequired,
    cancelBtnLabel: PropTypes.shape({}).isRequired,
    onReject: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    onResolve: PropTypes.func.isRequired,
    ...validationPropTypes,
    ...validationDefaultProps,
};

export default withValidation(EditAccountDetailsPopUp);